import React from 'react';
import AppleIcon from '@mui/icons-material/Apple';
import AndroidIcon from '@mui/icons-material/Android';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Typography,
  Box,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
} from '@mui/material';

import { QRCodeDisplayer } from '../../components';
import { SecretKeyDisplayer } from './SecretKeyDisplayer';
import { mfaColors, mfaQRContainerStyles } from '../styles';

const AUTH_APPS = [
  {
    name: 'Google Authenticator',
    ios: 'https://apps.apple.com/app/google-authenticator/id388497605',
    android: 'https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2',
  },
  {
    name: 'Microsoft Authenticator',
    ios: 'https://apps.apple.com/app/microsoft-authenticator/id983156458',
    android: 'https://play.google.com/store/apps/details?id=com.azure.authenticator',
  },
  {
    name: 'Authy',
    ios: 'https://apps.apple.com/app/authy/id494168017',
    android: 'https://play.google.com/store/apps/details?id=com.authy.authy',
  },
];

const AppDownloadRow = ({ app }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      py: 1,
      borderBottom: `1px solid ${mfaColors.border}`,
      '&:last-child': { borderBottom: 'none' },
    }}
  >
    <Typography variant="body2" sx={{ color: mfaColors.text }}>
      {app.name}
    </Typography>
    <Box sx={{ display: 'flex', gap: 0.5 }}>
      <Tooltip title="iOS" arrow>
        <IconButton
          size="small"
          component="a"
          href={app.ios}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: mfaColors.textMuted, '&:hover': { color: mfaColors.text } }}
        >
          <AppleIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Android" arrow>
        <IconButton
          size="small"
          component="a"
          href={app.android}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: mfaColors.textMuted, '&:hover': { color: '#3DDC84' } }}
        >
          <AndroidIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  </Box>
);

const AuthAppsAccordion = () => (
  <Accordion
    elevation={0}
    sx={{
      backgroundColor: mfaColors.background,
      border: `1px solid ${mfaColors.border}`,
      borderRadius: '8px !important',
      '&:before': { display: 'none' },
    }}
  >
    <AccordionSummary
      expandIcon={<ExpandMoreIcon sx={{ color: mfaColors.textMuted }} />}
      sx={{ minHeight: 48, '& .MuiAccordionSummary-content': { my: 1.5 } }}
    >
      <Typography variant="body2" sx={{ color: mfaColors.text, fontWeight: 500 }}>
        Need an authenticator app?
      </Typography>
    </AccordionSummary>
    <AccordionDetails sx={{ pt: 0, pb: 2, px: 2 }}>
      <Stack>
        {AUTH_APPS.map((app) => (
          <AppDownloadRow key={app.name} app={app} />
        ))}
      </Stack>
    </AccordionDetails>
  </Accordion>
);

export const SetupAuthenticatorStep = ({ secret, qrCode }) => (
  <Stack spacing={3} alignItems="center" sx={{ width: '100%' }}>
    <Typography variant="body2" sx={{ color: mfaColors.textMuted, textAlign: 'center' }}>
      Open your authenticator app and scan the QR code below
    </Typography>

    <Box sx={mfaQRContainerStyles}>
      <QRCodeDisplayer url={qrCode} size={160} />
    </Box>

    <Box sx={{ width: '100%' }}>
      <AuthAppsAccordion />
    </Box>

    <SecretKeyDisplayer secretKey={secret?.secretKey} />
  </Stack>
);
